<template>
  <v-dialog v-model="dialog" max-width="700" persistent>
    <v-card>
      <v-card-title class="headline">
        Responder preguntas de entrevista
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          Completa las respuestas basadas en la entrevista preliminar con el candidato:
        </p>
        <div v-if="loadingQuestions" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" />
          <p class="mt-2 caption">Cargando preguntas...</p>
        </div>
        <v-form v-else-if="questionsToAnswer.length > 0" ref="answerForm">
          <div 
            v-for="question in questionsToAnswer" 
            :key="question.DedValue"
            class="mb-4"
          >
            <!-- Pregunta de texto -->
            <s-text
              v-if="question.DedFormat === 'text'"
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :rules="getValidationRules(question)"
              outlined
              dense
            />

            <!-- Pregunta de número -->
            <s-text
              v-else-if="question.DedFormat === 'number'"
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :rules="getValidationRules(question)"
              type="number"
              outlined
              dense
            />

            <!-- Pregunta de checkbox boolean -->
            <v-checkbox
              v-else-if="question.DedFormat === 'boolean[checkbox]'"
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :true-value="'Sí'"
              :false-value="'No'"
              dense
            />

            <!-- Pregunta de definición múltiple (DedHelper2 = '1') -->
            <s-select-definition
              v-else-if="question.DedFormat === 'def' && question.DedHelper && question.DedHelper2 === '1'"
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :def="parseInt(question.DedHelper)"
              multiple
              badgesBelow
              clearable
              outlined
              dense
              chips
              small-chips
              deletable-chips
              :addCustom = "$fun.isAdmin()"
                :customFields="[
                  {
                    key: 'DedDescription',
                    label: 'Nombre',
                    required: true,
                    cols: 6,
                  }
                ]"
            />

            <!-- Pregunta de definición simple -->
            <s-select-definition
              v-else-if="question.DedFormat === 'def' && question.DedHelper"
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :def="parseInt(question.DedHelper)"
              :rules="getValidationRules(question)"
              outlined
              dense
              :addCustom = "$fun.isAdmin()"
                :customFields="[
                  {
                    key: 'DedDescription',
                    label: 'Nombre',
                    required: true,
                    cols: 6,
                  }
                ]"
            />

            <!-- Pregunta del componente customizado -->
            <div v-else-if="question.DedFormat === 'Component'" class="mb-2">
              <v-label class="d-block mb-1"><b>{{ getLabel(question) }}</b></v-label>
              <component
                :is="getComponentName(question.DedHelper)"
                v-model="answers[question.DedValue]"
              />
            </div>

            <!-- Pregunta de textarea (fallback) -->
            <v-textarea
              v-else
              v-model="answers[question.DedValue]"
              :label="getLabel(question)"
              :rules="getValidationRules(question)"
              outlined
              dense
              rows="2"
            />
          </div>
        </v-form>
        <div v-else class="text-center py-4">
          <p class="grey--text">No hay preguntas configuradas</p>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancel">Cancelar</v-btn>
        <v-btn 
          color="primary" 
          :loading="loading" 
          @click="confirm"
          :disabled="questionsToAnswer.length === 0"
        >
          Confirmar y avanzar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import HmnRecruitmentProcessService from "@/services/HumanResource/RecruitmentProcess/HmnRecruitmentProcessService";
import SLevelEducationComponent from "@/components/Utils/RecursosHm/SLevelEducationComponent.vue";

export default {
  name: "HmnInterviewResponsesForm",
  components: {
    SLevelEducationComponent
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    questions: {
      type: Array,
      default: () => []
    },
    allQuestions: {
      type: Array,
      default: () => []
    },
    rprID: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      answers: {},
      allQuestionsFromDef: [],
      loadingQuestions: false
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    questionsToAnswer() {
      // Filtra las preguntas basadas en los IDs en this.questions
      if (!Array.isArray(this.questions) || this.questions.length === 0) {
        return [];
      }
      if (!Array.isArray(this.allQuestionsFromDef) || this.allQuestionsFromDef.length === 0) {
        return [];
      }
      const selectedIds = this.questions
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      return this.allQuestionsFromDef.filter((q) =>
        selectedIds.includes(Number(q.DedValue))
      );
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        // ✅ Cargar preguntas del servidor via endpoint
        this.loadQuestionsFromEndpoint();
      }
    }
  },
  methods: {
    loadQuestionsFromEndpoint() {
      // Primero intentar usar el cache del padre
      if (Array.isArray(this.allQuestions) && this.allQuestions.length > 0) {
        this.allQuestionsFromDef = this.allQuestions;
        this.initializeAnswers();
        return;
      }

      // Si no hay cache, cargar del servidor usando RprID
      this.loadingQuestions = true;
      HmnRecruitmentProcessService.getInterviewQuestions(this.rprID, this.$fun.getUserID())
        .then((response) => {
          if (response.status === 200 && Array.isArray(response.data)) {
            this.allQuestionsFromDef = response.data;
            this.initializeAnswers();
          }
        })
        .catch((error) => {
          console.error('Error cargando preguntas:', error);
          this.$fun.alert('Error al cargar las preguntas', 'error');
        })
        .finally(() => {
          this.loadingQuestions = false;
        });
    },
    isMultiDef(question) {
      return question.DedFormat === 'def' && question.DedHelper2 === '1';
    },
    initializeAnswers() {
      this.answers = {};
      this.questionsToAnswer.forEach(q => {
        let defaultValue;
        if (q.DedFormat === 'boolean[checkbox]') {
          defaultValue = 'No';
        } else if (this.isMultiDef(q)) {
          defaultValue = [];
        } else {
          defaultValue = '';
        }
        this.$set(this.answers, q.DedValue, defaultValue);
      });
    },
    getLabel(question) {
      return question.DedDescription;
    },
    getComponentName(helper) {
      if (helper && helper.endsWith('.vue')) {
        return helper.replace('.vue', '');
      }
      return helper;
    },
    getValidationRules(question) {
      return [];
    },
    confirm() {
      if (this.$refs.answerForm && !this.$refs.answerForm.validate()) {
        this.$fun.alert("Completa los campos obligatorios", "warning");
        return;
      }

      // Serializar multi-selects como string separado por coma
      const serialized = {};
      this.questionsToAnswer.forEach((q) => {
        const val = this.answers[q.DedValue];
        if (this.isMultiDef(q) && Array.isArray(val)) {
          serialized[q.DedValue] = val.join(", ");
        } else {
          serialized[q.DedValue] = val;
        }
      });

      this.$emit('confirm', serialized);
    },
    cancel() {
      this.answers = {};
      this.dialog = false;
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}
</style>
